import { BaseQueryApi } from '@reduxjs/toolkit/dist/query';

import { COLLECTION_IDS } from '@/constants/collections';
import {
  modelsUseAggregatePriceSelector,
  modelsUseGroupingByBrandSelector,
} from '@/features/config/selectors';
import configSelector from '@/features/config/selectors/config';
import flattenFilterGridItemsSelector from '@/features/config/selectors/flattenFilterGridItems';
import immutableFilterQueriesSelector from '@/features/filters/selectors/immutableQueries';
import getExtendedItems from '@/features/references/helpers/getExtendedItems';
import type { RootStateType } from '@/features/store';
import { FilterBlocksEnum } from '@/interfaces/constructor.interfaces';
import { FiltersRawItemType, IFilterModel } from '@/interfaces/filters.interfaces';
import { IMedia } from '@/interfaces/grpc.interfaces';
import {
  ExtendedMarketingInfoItemType,
  MarketingInfoItemType,
} from '@/interfaces/marketingInfo';
import { IGrpcService } from '@/services/GrpcService';
import injectContainer from '@/services/InjectContainer';
import ServiceTypes from '@/services/ServiceTypes';
import deepCopy from '@/utils/deepCopy';
import instanceOfReference from '@/utils/instanceOfReference';

import { FiltersParametersType } from '../types';

import prepareFilterQuery from './prepareFilterQuery';

const MODEL_MEDIA_FILE_KEY = 'model_cars';
const MODEL_PARAMS_POSITIONING_ID = 'positioning';

/**
 * Запрос на получение дополнительных данных для значений фильтров
 * @param api
 * @param filtersRawItem {object} Данные по фильтрам
 * @returns Данные по фильтрам с дополнительными полями у значений фильтров
 */
const fetchFiltersWithExtraData = async (
  api: BaseQueryApi,
  filtersRawItem: FiltersRawItemType,
) => {
  const state = api.getState() as RootStateType;

  const config = configSelector(state);
  const filterGridItems = flattenFilterGridItemsSelector(state);
  const immutableFilterQueries = immutableFilterQueriesSelector(state);
  const useAggregatePrice = modelsUseAggregatePriceSelector(state);
  const useGroupingByBrand = modelsUseGroupingByBrandSelector(state);

  const grpcService = injectContainer.get<IGrpcService>(ServiceTypes.Grpc);
  const { itemsClient } = grpcService;

  const filtersRawWithExtraDataItem = deepCopy(filtersRawItem);

  const { data } = filtersRawWithExtraDataItem;

  const promises: Array<Promise<void>> = [];

  for (const [key, values] of Object.entries(data)) {
    const filterGridItem = filterGridItems.find(item => item.key === key);

    if (!filterGridItem) {
      continue;
    }

    switch (filterGridItem.type) {
      // Для фильтра по моделям необходимо получить
      // маркетинговую информацию и минимальную цену (при включенной настройке)
      case FilterBlocksEnum.FilterModelCheckboxGroup: {
        if (!Array.isArray(values) || !values.length) {
          break;
        }

        if (!instanceOfReference(values[0])) {
          break;
        }

        const filterModelValues = values as IFilterModel[];

        // Формируем параметры запросы на получение маркетинговой информации по модели
        const marketingInfoForModelsParameters: FiltersParametersType = {
          spaceId: config.spaceId,
          envId: config.envId,
          collectionId: COLLECTION_IDS.vehiclesMarketingInfo,
          options: {
            options: {
              sort: ['-position'],
            },
          },
          filter: {
            q: [
              `for.collection_id == "${
                COLLECTION_IDS.vehiclesModels
              }" && for.id in ["${filterModelValues.map(item => item.id).join(`", "`)}"]`,
            ],
          },
        };

        // Promise на добавление доп. маркетинговой информации для каждого
        // из значений для фильтра по моделям
        const marketingInfoForModelsPromise = itemsClient
          .findPublished(marketingInfoForModelsParameters)
          .then(response => {
            const marketingInfoItems = response.items as MarketingInfoItemType[];

            return getExtendedItems<MarketingInfoItemType, ExtendedMarketingInfoItemType>(
              api,
              marketingInfoItems,
            );
          })
          .then(extendedMarketingInfoItems => {
            const marketingInfoForModelsMap = new Map<
              string,
              ExtendedMarketingInfoItemType
            >();

            for (const extendedMarketingInfoItem of extendedMarketingInfoItems) {
              const { for: references } = extendedMarketingInfoItem.data;
              const modelReference = references?.find(
                ref => ref.collection_id === COLLECTION_IDS.vehiclesModels,
              );

              if (!modelReference || marketingInfoForModelsMap.get(modelReference.id)) {
                continue;
              }

              marketingInfoForModelsMap.set(modelReference.id, extendedMarketingInfoItem);
            }
            return marketingInfoForModelsMap;
          })
          .then(marketingInfoForModelsMap => {
            for (const filterModelValue of filterModelValues) {
              const marketingInfoForModel = marketingInfoForModelsMap.get(
                filterModelValue.id,
              );

              if (!marketingInfoForModel) {
                continue;
              }

              const { data: marketingInfoForModelData } = marketingInfoForModel;

              const marketingPositioning = marketingInfoForModelData?.params?.find(
                param => param.id === MODEL_PARAMS_POSITIONING_ID,
              );

              const modelMarketingMedia = marketingInfoForModelData.media
                ?.flatMap<IMedia>(({ additional, ...media }) => {
                  return (
                    additional?.map(({ key, file }) => ({
                      ...media,
                      id: key,
                      file,
                    })) || []
                  );
                })
                .find(media => {
                  if (!media?.file) {
                    return false;
                  }
                  return media.id === MODEL_MEDIA_FILE_KEY;
                });
              const marketingMedia =
                modelMarketingMedia ?? marketingInfoForModelData.media?.[0];

              Object.assign(filterModelValue, {
                marketingPrice: marketingInfoForModelData?.priceMin,
                marketingPositioning: marketingPositioning?.value,
                marketingMedia,
              });
            }
          });

        promises.push(marketingInfoForModelsPromise);

        if (useAggregatePrice) {
          for (const filterModelValue of filterModelValues) {
            // Формируем параметры запросы на получение минимальной цены по модели
            const aggregatePriceParameters: FiltersParametersType = {
              spaceId: config.spaceId,
              envId: config.envId,
              collectionId: COLLECTION_IDS.vehiclesVehicles,
              options: {
                fields: {
                  price: 'min(price)',
                  minPrice: 'min(minPrice)',
                },
              },
              filter: {
                ...immutableFilterQueries,
                q: [
                  prepareFilterQuery(filterGridItem, values, [filterModelValue.id]) || '',
                  ...immutableFilterQueries.q,
                ],
              },
            };

            // Promise на добавление доп. информации по цене каждого
            // из значений для фильтра по моделям
            const aggregatePricePromise = itemsClient
              .aggregatePublished(aggregatePriceParameters)
              .then(aggregatePriceResponse => {
                const { result } = aggregatePriceResponse;

                const price = result?.minPrice || result?.price || 0;

                Object.assign(filterModelValue, {
                  price,
                });
              });

            promises.push(aggregatePricePromise);
          }
        }

        if (useGroupingByBrand) {
          // Формируем параметры запросы на получение маркетинговой информации по бренду
          const marketingInfoForBrandsParameters: FiltersParametersType = {
            spaceId: config.spaceId,
            envId: config.envId,
            collectionId: COLLECTION_IDS.vehiclesMarketingInfo,
            options: {
              options: {
                sort: ['-position'],
              },
            },
            filter: {
              q: [
                `for.collection_id == "${
                  COLLECTION_IDS.vehiclesBrands
                }" && for.id in ["${filterModelValues
                  .map(v => v.brand.id)
                  .join(`", "`)}"]`,
              ],
            },
          };

          // Promise на добавление доп. маркетинговой информации для каждого
          // из значений для фильтра по моделям
          const marketingInfoForBrandsPromise = itemsClient
            .findPublished(marketingInfoForBrandsParameters)
            .then(marketingInfoResponse => {
              const marketingInfoItems =
                marketingInfoResponse.items as MarketingInfoItemType[];

              return getExtendedItems<
                MarketingInfoItemType,
                ExtendedMarketingInfoItemType
              >(api, marketingInfoItems);
            })
            .then(extendedMarketingInfoItems => {
              const marketingInfoForBrandsMap = new Map<
                string,
                ExtendedMarketingInfoItemType
              >();

              for (const extendedMarketingInfoItem of extendedMarketingInfoItems) {
                const { for: references } = extendedMarketingInfoItem.data;
                const brandReference = references?.find(
                  ref => ref.collection_id === COLLECTION_IDS.vehiclesBrands,
                );

                if (!brandReference || marketingInfoForBrandsMap.get(brandReference.id)) {
                  continue;
                }

                marketingInfoForBrandsMap.set(
                  brandReference.id,
                  extendedMarketingInfoItem,
                );
              }
              return marketingInfoForBrandsMap;
            })
            .then(marketingInfoForBrandsMap => {
              for (const filterModelValue of filterModelValues) {
                const marketingInfoForBrand = marketingInfoForBrandsMap.get(
                  filterModelValue.brand.id,
                );

                if (!marketingInfoForBrand) {
                  continue;
                }

                const { data: marketingInfoForBrandData } = marketingInfoForBrand;

                const brandPositioning = marketingInfoForBrandData?.params?.find(
                  param => param.id === MODEL_PARAMS_POSITIONING_ID,
                );

                const brandMedia = marketingInfoForBrandData.media
                  ?.flatMap<IMedia & { key: string }>(({ additional, ...media }) => {
                    return (
                      additional?.map(({ key, file }) => ({
                        ...media,
                        key,
                        file,
                      })) || []
                    );
                  })
                  .find(media => {
                    if (!media?.file) {
                      return false;
                    }
                    return media.key === MODEL_MEDIA_FILE_KEY;
                  });

                Object.assign(filterModelValue, {
                  brandPositioning: brandPositioning?.value,
                  brandMedia,
                });
              }
            });

          const extendedBrandsPromise = getExtendedItems(api, [
            {
              ...filtersRawItem,
              data: {
                brand: filterModelValues.map(v => v.brand),
              },
            },
          ]).then(([brandItem]) => {
            const brands = brandItem.data.brand || [];

            for (const filterModelValue of filterModelValues) {
              const brand = brands.find(b => b.id === filterModelValue.brand.id);

              Object.assign(filterModelValue, {
                extendedBrand: brand,
              });
            }
          });

          promises.push(marketingInfoForBrandsPromise, extendedBrandsPromise);
        }

        break;
      }

      default: {
        break;
      }
    }
  }

  await Promise.all(promises);

  return filtersRawWithExtraDataItem;
};

export default fetchFiltersWithExtraData;
